import { html, css } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';
import { msg } from '@omegagrid/localize';
import { dom } from '@omegagrid/core';
import type { GridEvent, TableTheme } from '@omegagrid/grid';
import type { ThemeEditor } from '@omegagrid/editor';
import type { TreeDropdown, TreeEvent } from '@omegagrid/tree';
import { StyleListItem, TableThemeRef, TableViewOptions } from '@reporting/rp-types/analytics';
import { BaseVisualizationEditor } from './baseEditor';
import type { TableVisualization } from '../table';
import { ThemeCodeEditorAdapter } from '../../../adapters/themeCodeEditorAdapter';
import rp, { CodeEditor, cloneTableTheme, isTableThemeRef } from '@reporting/rp-app-host';

const CUSTOM_THEME_KEY = '__custom';

/**
 * Visualization editor of the 'table' view: picks a table-wide theme from the
 * stored table styles (P_STYLE2) or edits an inline definition in an embedded
 * og-editor-theme. Editing a stored theme copies its definition into
 * view_options.theme (the report loses the P_STYLE2 reference); users with the
 * definition right can save the inline definition back as a new stored theme.
 */
@customElement('rp-visual-table-editor')
export class TableVisualizationEditor extends BaseVisualizationEditor<TableVisualization> {

	static styles = [...BaseVisualizationEditor.styles, css`
		.row og-button, og-button {
			width: 20px;
		}

		.names {
			display: grid;
			grid-template-columns: auto 1fr;
			gap: 4px;
			align-items: center;
			font-size: 11px;
			color: var(--og-text-color-3);
		}

		.names label {
			opacity: .7;
		}
	`];

	@state()
	private styleItems: StyleListItem[];

	@state()
	private editing = false;

	/** row/column names of the last cell clicked while editing, for authoring matchers */
	@state()
	private selectedCell: {row?: string, column?: string};

	/** raw view: the inline definition is edited as JSON in rp-editor-code */
	@state()
	private rawMode = false;

	private _stylesPromise: Promise<void>;

	get viewOptions() {
		return super.viewOptions as TableViewOptions;
	}

	/** the inline theme definition, null when the theme is a stored reference or absent */
	private get inlineTheme(): Record<string, any>|null {
		const theme = this.viewOptions?.theme;
		return theme && !isTableThemeRef(theme) ? theme : null;
	}

	willUpdate() {
		this.grid = false;
		this._stylesPromise ??= this.loadStyles();
	}

	private async loadStyles(reload = false) {
		try {
			this.styleItems = await rp.ms('analytics').get<StyleListItem[]>('styles', reload ? {t: Date.now()} : undefined) ?? [];
		} catch (error) {
			this.styleItems = [];
			rp.log.error(error);
		}
	}

	private get selectedKey(): string {
		if (this.inlineTheme) return CUSTOM_THEME_KEY;
		const theme = this.viewOptions?.theme as TableThemeRef;
		return theme?.id_style != null ? String(theme.id_style) : 'none';
	}

	/**
	 * The embedded theme editor, created imperatively and cached - og-* editors
	 * lose their state when lit recreates them. It mutates the passed theme
	 * object (= view_options.theme) in place and signals edits via theme.change.
	 */
	private _themeEditor: ThemeEditor;
	private get themeEditor() {
		if (this._themeEditor) return this._themeEditor;
		this._themeEditor = dom.createElement<ThemeEditor>('og-editor-theme', {
			listeners: [['theme.change', this._onThemeEdit]]
		});
		this._themeEditor.style.height = '100%';
		return this._themeEditor;
	}

	private _previewTimer: number;
	private _onThemeEdit = () => {
		clearTimeout(this._previewTimer);
		this._previewTimer = window.setTimeout(() => this.applyInlineTheme(), 300);
	}

	/**
	 * The raw JSON editor, cached like the theme editor. The adapter reads the
	 * working theme lazily and applies the parsed JSON on the editor's save.
	 */
	private _codeEditor: CodeEditor;
	private get codeEditor() {
		if (this._codeEditor) return this._codeEditor;
		this._codeEditor = dom.createElement<CodeEditor>('rp-editor-code', {
			adapter: new ThemeCodeEditorAdapter(
				() => this.inlineTheme,
				theme => this.applyRawTheme(theme),
			)
		});
		this._codeEditor.style.height = '100%';
		return this._codeEditor;
	}

	/** the parsed raw JSON becomes the inline definition, preview follows */
	private applyRawTheme(theme: Record<string, any>) {
		this.viewOptions.theme = theme;
		this.applyInlineTheme();
		this.requestUpdate();
	}

	private toggleRawMode = async () => {
		this.rawMode = !this.rawMode;
		if (!this.rawMode || !this._codeEditor) return;
		// re-read the working theme, the visual editor may have changed it
		// meanwhile - only after the cached editor is re-attached and rendered
		await this.updateComplete;
		await this._codeEditor.updateComplete;
		this._codeEditor.discard(true);
	}

	private applyInlineTheme() {
		const theme = this.inlineTheme;
		if (!theme) return;
		// a copy - the visualization applies dark-mode fixups and RegExp name
		// matchers on its own object, view_options stays serializable
		if (this.visualization) this.visualization.tableTheme = cloneTableTheme(theme as TableTheme);
		this.dispatchEvent(new CustomEvent('edit', {bubbles: true, composed: true}));
	}

	/** the theme picker tree, grouped into the built-in (global) and account branches */
	@query('og-tree-dropdown')
	private themeDropdown: TreeDropdown;

	private _themeItemsSignature: string;

	private updateThemeDropdown() {
		const dropdown = this.themeDropdown;
		if (!dropdown) return;

		const items = this.styleItems ?? [];
		const builtin = items.filter(s => s.accounting == -1);
		const others = items.filter(s => s.accounting != -1);

		const signature = JSON.stringify([!!this.inlineTheme, items]);
		if (signature !== this._themeItemsSignature) {
			this._themeItemsSignature = signature;
			dropdown.setSourceData([
				{k: 'none', v: `<${msg('None')}>`, level: 0},
				...(this.inlineTheme ? [{k: CUSTOM_THEME_KEY, v: `<${msg('Custom')}>`, level: 0}] : []),
				...(others.length ? [
					{k: 'group-local', v: msg('Local'), level: 0, s: false},
					...others.map(s => ({k: String(s.id_style), v: s.description, level: 1})),
				] : []),
				...(builtin.length ? [
					{k: 'group-builtin', v: msg('Built-in'), level: 0, s: false},
					...builtin.map(s => ({k: String(s.id_style), v: s.description, level: 1})),
				] : []),
			], {selectMode: 'single', checkboxes: false, autoClose: true});
		}

		dropdown.value = [[this.selectedKey, 0, 1]];
	}

	private _onThemeSelect = (e: TreeEvent) => {
		const key = e.node?.key;
		if (!key || key.startsWith('group-')) {
			// a branch node - keep the current selection
			this.updateThemeDropdown();
			return;
		}
		this.selectTheme(key);
	}

	private async selectTheme(value: string) {
		if (value == CUSTOM_THEME_KEY) {
			this.editing = true;
			return;
		}

		this.editing = false;
		const idStyle = value && value != 'none' ? Number(value) : null;

		if (!idStyle) {
			delete this.viewOptions.theme;
			if (this.visualization) this.visualization.tableTheme = null;
		} else {
			this.viewOptions.theme = { id_style: idStyle };
			try {
				const theme = await rp.ms('analytics').get<TableTheme>(`styles/${idStyle}`);
				if (this.visualization) this.visualization.tableTheme = theme;
			} catch (error) {
				rp.log.error(error);
			}
		}

		// no applyChange() - setting tableTheme already refreshes the grid; the
		// edit event commits the working context so the change survives a save
		this.dispatchEvent(new CustomEvent('edit', {bubbles: true, composed: true}));
		this.requestUpdate();
	}

	/** copy-on-edit: the selected stored theme (or an empty one) becomes the inline definition */
	private editTheme = async () => {
		if (!this.inlineTheme) {
			const theme = this.viewOptions?.theme as TableThemeRef;
			if (theme?.id_style != null) {
				try {
					const definition = await rp.ms('analytics').get<TableTheme>(`styles/${theme.id_style}`);
					const description = this.styleItems?.find(s => s.id_style == theme.id_style)?.description;
					this.viewOptions.theme = {...definition, name: definition.name || description || msg('Custom')};
				} catch (error) {
					rp.log.error(error);
					return;
				}
			} else {
				this.viewOptions.theme = {name: msg('Custom')};
			}
			this.applyInlineTheme();
		}

		this.themeEditor.theme = this.viewOptions.theme as TableTheme;
		this.editing = true;
	}

	/** saves the inline definition as a new stored theme (definition right required) */
	private saveAsTheme = async () => {
		const theme = this.inlineTheme;
		if (!theme) return;

		// the name is entered through the commands palette, like saving a context
		const defaultName = theme.name || msg('Theme');
		const cmd = await rp.host.openMenu({
			text: msg('Enter name for the new theme'),
			value: '',
			placeholder: defaultName,
			mode: 'input',
			content: [
				{id: 'save', name: msg('Save')},
				{id: 'cancel', name: msg('Cancel')},
			]
		});
		if (cmd?.id != 'save') return;

		// empty input falls back to the default name shown as placeholder
		theme.name = cmd.value?.length ? cmd.value : defaultName;

		try {
			const saved = await rp.ms('definition').post<StyleListItem>('styles/T', {
				description: theme.name,
				style: JSON.stringify(theme)
			});
			await this.loadStyles(true);
			if (saved?.id_style != null) {
				this.viewOptions.theme = {id_style: saved.id_style};
				this.editing = false;
				this.dispatchEvent(new CustomEvent('edit', {bubbles: true, composed: true}));
				this.requestUpdate();
			}
		} catch (error) {
			rp.log.error(error);
			rp.host.showAlert({type: 'danger', content: msg('Error saving theme')});
		}
	}

	/** the visualization currently held in theme edit mode (clicks inspect instead of drilling) */
	private _inspected: TableVisualization;

	private _onGridClick = (e: GridEvent) => {
		if (!e.cell) return;
		this.selectedCell = {row: e.cell.row?.name, column: e.cell.column?.name};
	}

	private syncThemeEditMode() {
		const target = this.editing && this.inlineTheme ? this.visualization : null;
		if (this._inspected === target) return;
		if (this._inspected) {
			this._inspected.themeEditMode = false;
			this._inspected.removeEventListener('grid.click', this._onGridClick);
		}
		this._inspected = target;
		if (target) {
			target.themeEditMode = true;
			target.addEventListener('grid.click', this._onGridClick);
		}
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.editing = false;
		this.syncThemeEditMode();
	}

	updated() {
		// keep the widget on the edited object (a new identity rebuilds its lists)
		if (this.editing && this.inlineTheme && this._themeEditor && this._themeEditor.theme !== this.inlineTheme) {
			this._themeEditor.theme = this.inlineTheme as TableTheme;
		}
		this.syncThemeEditMode();
		this.updateThemeDropdown();
	}

	layout() {
		this.codeEditor?.layout();
	}

	render = () => html`
		<style>${BaseVisualizationEditor.styles}</style>

		<div class="row" style="display: flex; gap: 4px; align-items: center">
			<og-tree-dropdown
				class="field"
				color="transparent"
				style="flex: 1"
				.caption="${msg('Theme')}"
				@change="${this._onThemeSelect}">
			</og-tree-dropdown>

			<og-button icon="pen" .tooltip="${msg('Edit')}" @click="${this.editTheme}"></og-button>

			${this.inlineTheme ? html`
				<og-button
					icon="code"
					type="toggle"
					state="${this.rawMode ? 'checked' : 'unchecked'}"
					.tooltip="${msg('Raw JSON')}"
					@click="${this.toggleRawMode}">
				</og-button>
			` : ''}

			${this.inlineTheme && rp.userProvider.hasRight('definition') ? html`
				<og-button icon="floppy-disk" .tooltip="${msg('Save as theme')}" @click="${this.saveAsTheme}"></og-button>
			` : ''}
		</div>

		${this.editing && this.inlineTheme ? html`
			<div class="row names" style="padding-bottom: 5px">
				<label>${msg('Row')}</label>
				<og-input class="name" readonly .value="${this.selectedCell?.row ?? ''}"></og-input>
				<label>${msg('Column')}</label>
				<og-input class="name" readonly .value="${this.selectedCell?.column ?? ''}"></og-input>
			</div>

			<div style="flex: 1; overflow: hidden">${this.rawMode ? this.codeEditor : this.themeEditor}</div>
		` : ''}
	`;

}
